/* eslint-disable no-unused-vars */
import React, { FC, useState } from "react";

import styled from "@emotion/styled";
import { Link, navigate } from "gatsby";

import * as yup from "yup";
import { useFormik } from "formik";

import PropTypes from "prop-types";
import classnames from "classnames";

import Input from "../Input";
import Textarea from "../Textarea";
import ReCAPTCHA from "../ReCAPTCHA";
import FileInput, { MAX_FILE_SIZE } from "../FileInput";

import { sendEmailWithValues, fileToBase64 } from "../../utils/send-email";

const TEMPLATE_ID = "mosano_job_application";

interface ApplicationFormValues {
  jobTitle: string;
  jobInfo: string;
  name: string;
  email: string;
  phone: string;
  about: string;
  attachment: File;
  reCaptchaToken: string;
}

const validationSchema = yup.object({
  jobTitle: yup.string(),
  jobInfo: yup.string(),
  name: yup.string().required("Name is required"),
  about: yup.string().required("About is required"),
  phone: yup.string().required("Phone is required"),
  reCaptchaToken: yup.string().required("reCaptcha validation is required"),
  email: yup
    .string()
    .email("Value must be a valid email")
    .required("Email is required"),
  attachment: yup
    .mixed<File>()
    .required("CV is required")
    .test({
      name: "fileSize",
      message: "File size is too large",
      test: (value) => value?.size <= MAX_FILE_SIZE,
    })
    .test({
      name: "fileType",
      message: "File must be a PDF",
      test: (value) => value?.type === "application/pdf",
    }),
});

export enum FormStatus {
  Idle = "IDLE",
  Sending = "SENDING",
  Sent = "SENT",
  Errored = "ERROR",
}

export interface ApplicationFormProps {
  dark?: boolean;
  jobTitle?: string;
  jobInfo?: string;
  containerType?: string;
  applicationType?: string;
}

const ApplicationForm: FC<ApplicationFormProps> = ({
  dark,
  jobTitle,
  jobInfo,
  applicationType,
}) => {
  const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.Idle);

  const darkClassName = dark ? "white" : "";
  const disabled =
    formStatus === FormStatus.Sending || formStatus === FormStatus.Sent;

  const formik = useFormik<ApplicationFormValues>({
    validateOnBlur: false,
    validateOnMount: false,
    validateOnChange: false,
    validationSchema,
    initialValues: {
      jobTitle: jobTitle || "Self-application",
      jobInfo: jobInfo || "",
      name: "",
      email: "",
      about: "",
      phone: "",
      attachment: null,
      reCaptchaToken: "",
    },
    onSubmit: async (values) => {
      if (disabled) return;
      setFormStatus(FormStatus.Sending);
      try {
        const attachmentBase64 = await fileToBase64(values.attachment);

        const finalValues = {
          ...values,
          attachment: attachmentBase64,
          "g-recaptcha-response": values.reCaptchaToken,
        };
        delete finalValues.reCaptchaToken;

        await sendEmailWithValues(finalValues, TEMPLATE_ID);
        setFormStatus(FormStatus.Sent);
        navigate("/thanks?service-page");
      } catch (err) {
        setFormStatus(FormStatus.Errored);
      }
    },
  });

  return (
    <div
      className={`container-${
        applicationType || "form"
      } max-width-application-form`}
    >
      <h1 className={`heading ${darkClassName}`}>
        {jobTitle || "Self-application"}
      </h1>
      <p className={`p1 full ${darkClassName}`}>{jobInfo || ""}</p>
      <div className="div-block-62"></div>
      <div className="form-block w-form">
        <form
          method="POST"
          id="application-form"
          name="application-form"
          encType="multipart/form-data"
          onSubmit={formik.handleSubmit}
        >
          <input type="hidden" name="jobTitle" value={formik.values.jobTitle} />
          <input type="hidden" name="jobInfo" value={formik.values.jobInfo} />
          <Input
            label="Name *"
            id="name"
            type="text"
            name="name"
            data-name="Name"
            placeholder="Your Name"
            className="text-field w-input"
            maxLength={128}
            disabled={disabled}
            dark={darkClassName}
            value={formik.values.name}
            error={formik.errors.name}
            onChange={formik.handleChange}
          />
          <ContactsRow>
            <div style={{ flex: 1 }}>
              <Input
                label="Email *"
                id="email"
                type="email"
                name="email"
                data-name="Email"
                placeholder="your.name@email.com"
                className="text-field w-input"
                maxLength={128}
                disabled={disabled}
                dark={darkClassName}
                value={formik.values.email}
                error={formik.errors.email}
                onChange={formik.handleChange}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                label="Phone Number *"
                id="phone"
                type="tel"
                name="phone"
                data-name="phone"
                placeholder="Your Phone Number"
                className="text-field w-input"
                maxLength={64}
                disabled={disabled}
                dark={darkClassName}
                value={formik.values.phone}
                error={formik.errors.phone}
                onChange={formik.handleChange}
              />
            </div>
          </ContactsRow>
          <Textarea
            label="Tell us about you *"
            id="about"
            name="about"
            data-name="about"
            className="text-field-copy w-input"
            maxLength={512}
            disabled={disabled}
            dark={darkClassName}
            value={formik.values.about}
            error={formik.errors.about}
            onChange={formik.handleChange}
          />
          <div style={{ height: "3rem" }} />
          <ReCaptchaRow>
            <FileInput
              id="file-input-application-form"
              required
              label="CV"
              disabled={disabled}
              dark={darkClassName}
              error={formik.errors.attachment as string}
              onChange={(file) => formik.setFieldValue("attachment", file)}
            />
            <ReCAPTCHA
              error={formik.errors.reCaptchaToken}
              sitekey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
              onChange={(token) =>
                formik.setFieldValue("reCaptchaToken", token)
              }
            />
          </ReCaptchaRow>
          <div className="div-block-64" style={{ marginTop: "48px" }}>
            <div className={`p3 small-2 ${darkClassName}`}>
              The information provided will be processed according to our{" "}
              <Link className={darkClassName} to="/privacypolicy">
                Privacy Policy
              </Link>
              .
            </div>
            <div className="div-block-66"></div>
            <span className={classnames("mosano-button-outer", { disabled })}>
              <div className="button-container-details">
                <button
                  type="submit"
                  data-wait="Please wait..."
                  className={`button-details hover-button w-button ${darkClassName}`}
                  style={{ width: "auto", whiteSpace: "nowrap" }}
                  disabled={disabled}
                >
                  submit application
                </button>
              </div>
            </span>
          </div>
        </form>
        <div
          className={`w-form-done ${darkClassName}`}
          style={{ display: formStatus === FormStatus.Sent && "block" }}
        >
          <div>Thank you! Your submission has been received!</div>
        </div>
        <div
          className={`w-form-fail ${darkClassName}`}
          style={{ display: formStatus === FormStatus.Errored && "block" }}
        >
          <div>
            Oops! Something went wrong while submitting the form. Please try
            again.
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin: 3rem 0;
  gap: 3rem;
`;

const ReCaptchaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export default ApplicationForm;

ApplicationForm.propTypes = {
  dark: PropTypes.bool,
  containerType: PropTypes.string,
  applicationType: PropTypes.string,
  jobTitle: PropTypes.string,
  jobInfo: PropTypes.string,
};
